import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Alert,
} from 'antd';
import {
  GiftOutlined,
  LoginOutlined,
  UserAddOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import eventBus from '../utils/eventBus';
import { getInviteStats, type InviteStats } from '../services/invite';
import { message } from 'antd';
import { useTranslation } from '../hooks/useTranslation';

const { Title, Text } = Typography;

const InviteWelcome: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null); // null 表示正在检查
  const [inviteCodeFromUrl, setInviteCodeFromUrl] = useState<string | null>(null);
  const [stats, setStats] = useState<InviteStats | null>(null);
  const [loading, setLoading] = useState(false);

  // 检查用户登录状态
  const checkLoginStatus = async () => {
    try {
      const response = await fetch('/login/info', {
        method: 'GET',
        credentials: 'include',
      });
      
      // 尝试解析 JSON（即使返回 400，也可能包含 JSON 数据）
      const result = await response.json();
      
      // 检查是否登录成功
      if (result.isSuccess && (result.email || result.detail?.username)) {
        setIsLoggedIn(true);
        return true;
      } else {
        // 未登录或登录失败
        setIsLoggedIn(false);
        return false;
      }
    } catch (error) {
      console.error('Check login status failed:', error);
      // 发生错误时也设置为未登录，避免一直显示加载中
      setIsLoggedIn(false);
      return false;
    }
  };

  // 从 URL 获取邀请码
  const getInviteCodeFromUrl = () => {
    // 在 HashRouter 中，需要使用 window.location.hash 而不是 react-router 的 location.hash
    // 格式：#/invite-welcome?invite=INV-XXXX
    const hash = window.location.hash;
    
    // 从 hash 中查找邀请码
    if (hash) {
      // 匹配 hash 中的 ?invite= 或 &invite=
      const hashMatch = hash.match(/[?&]invite=([^&]+)/);
      if (hashMatch && hashMatch[1]) {
        return decodeURIComponent(hashMatch[1]);
      }
    }
    
    // 如果 hash 中没有，尝试从 location.search 中查找（兼容其他路由配置）
    const search = window.location.search;
    if (search) {
      const searchParams = new URLSearchParams(search);
      const inviteCode = searchParams.get('invite');
      if (inviteCode) {
        return decodeURIComponent(inviteCode);
      }
    }
    
    return null;
  };

  // 加载邀请统计（已登录时）
  const loadData = async () => {
    setLoading(true);
    try {
      const statsData = await getInviteStats();
      setStats(statsData);
    } catch (error: any) {
      message.error(error.message || t('invite.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 打开登录弹框
  const handleOpenLogin = () => {
    eventBus.emit('openLoginModal');
  };

  // 复制邀请码
  const copyInviteCode = () => {
    if (stats?.inviteCode) {
      navigator.clipboard.writeText(stats.inviteCode);
      message.success(t('invite.codeCopied'));
    }
  };

  // 复制邀请链接
  const copyInviteLink = () => {
    if (stats?.inviteCode) {
      const baseUrl = window.location.origin + window.location.pathname;
      const inviteLink = `${baseUrl}#/invite-welcome?invite=${stats.inviteCode}`;
      navigator.clipboard.writeText(inviteLink);
      message.success(t('invite.linkCopied'));
    }
  };

  // 获取邀请链接
  const getInviteLink = () => {
    if (stats?.inviteCode) {
      const baseUrl = window.location.origin + window.location.pathname;
      return `${baseUrl}#/invite-welcome?invite=${stats.inviteCode}`;
    }
    return '';
  };

  // 初始化：检查登录状态和邀请码
  useEffect(() => {
    const inviteCode = getInviteCodeFromUrl();
    setInviteCodeFromUrl(inviteCode);
    
    const initCheck = async () => {
      try {
        const loggedIn = await checkLoginStatus();
        // 如果已登录，加载邀请统计信息
        if (loggedIn) {
          loadData();
        }
      } catch (error) {
        console.error('Initialization failed:', error);
        setIsLoggedIn(false);
      }
    };
    
    initCheck();
  }, [location]);

  // 如果正在检查登录状态
  if (isLoggedIn === null) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Text>{t('invite.welcome.loading')}</Text>
      </div>
    );
  }

  // 如果检测到邀请码且未登录，显示登录提示
  if (inviteCodeFromUrl && !isLoggedIn) {
    return (
      <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <GiftOutlined style={{ fontSize: '64px', color: '#1890ff' }} />
            <Title level={3}>{t('invite.welcome.welcomeTitle')}</Title>
            <Text type="secondary" style={{ fontSize: '16px', display: 'block' }}>
              {t('invite.welcome.loginReward')}
            </Text>
            <Button
              type="primary"
              size="large"
              icon={<LoginOutlined />}
              onClick={handleOpenLogin}
              style={{ marginTop: '24px' }}
            >
              {t('invite.welcome.clickLogin')}
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  // 如果检测到邀请码且已登录，显示提示信息和邀请链接
  if (inviteCodeFromUrl && isLoggedIn && stats) {
    return (
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <Alert
          message={t('invite.welcome.alreadyLoggedIn')}
          type="success"
          style={{ marginBottom: '24px' }}
        />
        
        {/* 邀请码和邀请链接 */}
        <Card style={{ marginBottom: '24px' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Title level={4}>{t('invite.welcome.myInviteCode')}</Title>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                <Text
                  code
                  style={{
                    fontSize: '20px',
                    padding: '8px 16px',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px',
                  }}
                >
                  {stats.inviteCode}
                </Text>
                <Button
                  type="primary"
                  icon={<CopyOutlined />}
                  onClick={copyInviteCode}
                >
                  {t('invite.welcome.copyInviteCode')}
                </Button>
              </div>
            </div>

            <div>
              <Title level={4}>{t('invite.welcome.inviteLink')}</Title>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                <Text
                  style={{
                    fontSize: '14px',
                    padding: '8px 16px',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px',
                    flex: 1,
                    wordBreak: 'break-all',
                  }}
                >
                  {getInviteLink()}
                </Text>
                <Button
                  type="primary"
                  icon={<CopyOutlined />}
                  onClick={copyInviteLink}
                >
                  {t('invite.welcome.copyLink')}
                </Button>
              </div>
            </div>
          </Space>
        </Card>
      </div>
    );
  }

  // 如果没有邀请码，显示默认提示
  return (
    <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <UserAddOutlined style={{ fontSize: '64px', color: '#1890ff' }} />
          <Title level={3}>{t('invite.welcome.inviteFriends')}</Title>
          <Text type="secondary" style={{ fontSize: '16px', display: 'block' }}>
            {t('invite.welcome.invalidLink')}
          </Text>
        </Space>
      </Card>
    </div>
  );
};

export default InviteWelcome;
